/**
===================================================
Module: frontend.src.components.SessionCard.tsx
Description: Session card component for displaying individual sessions
Owner: Kawada Technosystem Co Ltd
All Rights Reserved
This script is proprietary and confidential.
Unauthorized copying, distribution, or modification of this
file, via any medium, is strictly prohibited.
===================================================
*/

import React, { useState } from "react";
import { Session } from "../types";
import SessionDetails from "./SessionDetails";
import "../styles/SessionDetails.css";

interface SessionCardProps {
  session: Session;
  onClick: (sessionId: string) => void;
  isSelectionMode?: boolean;
  isSelected?: boolean;
  onSelect?: (sessionId: string, isSelected: boolean) => void;
  onUpdateSession?: (
    sessionId: string,
    updates: { name: string; description: string },
  ) => void;
}

const SessionCard: React.FC<SessionCardProps> = ({
  session,
  onClick,
  isSelectionMode = false,
  isSelected = false,
  onSelect,
  onUpdateSession,
}) => {
  const [showDetails, setShowDetails] = useState(false);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString("ja-JP", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: false,
    });
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case "completed":
        return "status-completed";
      case "failed":
        return "status-failed";
      case "in_progress":
      case "processing":
        return "status-processing";
      default:
        return "status-draft";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "completed":
        return "完了";
      case "failed":
        return "失敗";
      case "in_progress":
      case "processing":
        return "処理中";
      case "queued":
        return "処理待ち";
      case "uploading":
        return "アップロード中";
      case "waiting for upload":
        return "アップロード待ち";
      default:
        return "Draft";
    }
  };

  const hasThumbnail =
    session.thumbnail_data && session.thumbnail_data.length > 0;

  const getThumbnailUrl = () => {
    if (!hasThumbnail) return null;

    if (
      typeof session.thumbnail_data === "string" &&
      session.thumbnail_data.startsWith("data:")
    ) {
      return session.thumbnail_data;
    }

    return null;
  };

  const thumbnailUrl = getThumbnailUrl();

  const handleCardClick = () => {
    if (isSelectionMode) {
      onSelect?.(session.id, !isSelected);
    } else {
      if (session.status === "completed") {
        setShowDetails(true);
      } else {
        onClick(session.id);
      }
    }
  };

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect?.(session.id, !isSelected);
  };

  const handleUpdateSession = async (
    sessionId: string,
    updates: { name: string; description: string },
  ) => {
    if (onUpdateSession) {
      await onUpdateSession(sessionId, updates);
    }
  };

  return (
    <>
      <div
        className={`session-card ${isSelectionMode ? "selection-mode" : ""}`}
        onClick={handleCardClick}
      >
        {isSelectionMode && (
          <div className="selection-checkbox" onClick={handleCheckboxClick}>
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => {}}
              className="session-checkbox"
            />
          </div>
        )}

        <div className="session-thumbnail">
          {thumbnailUrl ? (
            <img
              src={thumbnailUrl}
              alt={`Thumbnail for ${session.name}`}
              className="thumbnail-image"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ) : (
            <div className="thumbnail-placeholder">
              <span></span>
            </div>
          )}
        </div>

        <div className="session-info">
          <div className="session-header">
            <h3 className="session-name">
              {session.name || "Untitled Session"}
            </h3>
            <span className={`status-badge ${getStatusClass(session.status)}`}>
              {getStatusText(session.status)}
            </span>
          </div>

          <div className="session-description">
            {session.description || "説明なし"}
          </div>

          <div className="session-date">{formatDate(session.created_at)}</div>
        </div>
      </div>

      {showDetails && session.status === "completed" && (
        <SessionDetails
          session={session}
          onClose={() => setShowDetails(false)}
          onUpdate={handleUpdateSession}
        />
      )}
    </>
  );
};

export default SessionCard;
